import { Box, Button, FormControl, InputLabel, MenuItem, Select, TextField, Tab, Typography, Accordion, AccordionSummary, AccordionDetails, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Switch, FormControlLabel, Menu, Divider, Paper, List, ListItem, ListItemText, ListItemSecondaryAction, CircularProgress, Alert, Stack } from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { useState, useEffect } from "react";
import ProjectHttpClient from "../../../../core/infrastructure/clients/v1/ProjectHttpClient";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { ExecTabs } from "../../../company/styles/AdvisorStyles";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import MoveDownIcon from '@mui/icons-material/MoveDown';
import ClientProvider from "../../../../modules/ClientProvider";
import { useAdvisorShares } from "../../hooks/useAdvisorShares";
import { IAdvisorActivity } from "../../../../types/database";

// Field Mapping Types
export type MappingType = 'direct' | 'option_mapping' | 'boolean_convert' | 'regex_extract' | 'numeric_extract';

export type StandardFieldName =
    | 'beta_interest'
    | 'demo_interest'
    | 'advising_interest'
    | 'followup_interest'
    | 'investor_interest'
    | 'employment_interest'
    | 'max_spend'
    | 'role'
    | 'demand'
    | 'company_size'
    | 'company_revenue';

export interface FieldMappingCondition {
    question_id?: number;
    answer_contains?: string;
    answer_equals?: string;
}

export interface BaseFieldMapping {
    type: MappingType;
    default_value?: string;
    condition?: FieldMappingCondition;
}

export interface DirectMapping extends BaseFieldMapping {
    type: 'direct';
}

export interface OptionMapping extends BaseFieldMapping {
    type: 'option_mapping';
    value_mappings: { [inputValue: string]: string };
}

export interface BooleanConvertMapping extends BaseFieldMapping {
    type: 'boolean_convert';
    true_values?: string[];
    false_values?: string[];
}

export interface RegexExtractMapping extends BaseFieldMapping {
    type: 'regex_extract';
    regex_pattern: string;
}

export interface NumericExtractMapping extends BaseFieldMapping {
    type: 'numeric_extract';
}

export type FieldMapping =
    | DirectMapping
    | OptionMapping
    | BooleanConvertMapping
    | RegexExtractMapping
    | NumericExtractMapping;

export interface QuestionFieldMappings {
    // Standard business logic fields
    beta_interest?: FieldMapping;
    demo_interest?: FieldMapping;
    advising_interest?: FieldMapping;
    followup_interest?: FieldMapping;
    investor_interest?: FieldMapping;
    employment_interest?: FieldMapping;
    max_spend?: FieldMapping;
    role?: FieldMapping;
    demand?: FieldMapping;
    company_size?: FieldMapping;
    company_revenue?: FieldMapping;

    // Custom fields - any string key
    custom_fields?: { [customFieldName: string]: FieldMapping };
}

export interface FieldMappingFormData {
    fieldName: StandardFieldName | string;
    isCustomField: boolean;
    customFieldName?: string;
    mapping: FieldMapping;
}

export const MAPPING_TYPE_OPTIONS = [
    { value: 'direct', label: 'Direct (use answer as-is)', description: 'No transformation applied' },
    { value: 'option_mapping', label: 'Option Mapping', description: 'Map specific values to outputs' },
    { value: 'boolean_convert', label: 'Boolean Convert', description: 'Convert to true/false' },
    { value: 'regex_extract', label: 'Regex Extract', description: 'Extract using pattern matching' },
    { value: 'numeric_extract', label: 'Numeric Extract', description: 'Extract first number found' }
] as const;

export const STANDARD_FIELD_OPTIONS = [
    { value: 'beta_interest', label: 'Beta Interest', description: 'Interest in beta testing' },
    { value: 'demo_interest', label: 'Demo Interest', description: 'Interest in product demos' },
    { value: 'advising_interest', label: 'Advising Interest', description: 'Interest in advisory roles' },
    { value: 'followup_interest', label: 'Follow-up Interest', description: 'Interest in follow-up contact' },
    { value: 'investor_interest', label: 'Investor Interest', description: 'Interest in investment opportunities' },
    { value: 'employment_interest', label: 'Employment Interest', description: 'Interest in job opportunities' },
    { value: 'max_spend', label: 'Maximum Spend', description: 'Budget/spending capacity' },
    { value: 'role', label: 'Role', description: 'Decision-making role' },
    { value: 'demand', label: 'Demand', description: 'Purchase likelihood/demand' },
    { value: 'company_size', label: 'Company Size', description: 'Size of respondent company' },
    { value: 'company_revenue', label: 'Company Revenue', description: 'Revenue of respondent company' }
] as const;

const touchpointTypes = [
    { value: "default", label: "Default" },
    { value: "manual", label: "Manual" },
    { value: "auto", label: "Automated" }
];

const touchpointStatuses = [
    { value: "draft", label: "Draft" },
    { value: "scheduled", label: "Scheduled" },
    { value: "active", label: "Active" },
    { value: "completed", label: "Completed" }
];

export const TouchpointForm = ({ project, touchpointUuid, editMode, setEditMode, reloadTouchpoints }: any) => {
    const { getAccessTokenSilently } = useAuth0();
    const client = new ProjectHttpClient();
    const [currentTab, setCurrentTab] = useState(0);
    const { availableActivities, fetched } = useAdvisorShares(project.id);
    const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
    const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
    const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState<any>(null);
    const [editingQuestionSectionId, setEditingQuestionSectionId] = useState<string | null>(null);
    const [moveQuestionMenuOpen, setMoveQuestionMenuOpen] = useState<string | null>(null);
    const [moveQuestionMenuAnchor, setMoveQuestionMenuAnchor] = useState<HTMLElement | null>(null);
    const [reloadResultsDialogOpen, setReloadResultsDialogOpen] = useState(false);
    const [reloadResults, setReloadResults] = useState<any>(null);
    const [isReloading, setIsReloading] = useState(false);
    const [touchpoint, setTouchpoint] = useState<any>(null);
    const [isLoadingTouchpoint, setIsLoadingTouchpoint] = useState(false);
    const [touchpointLoadError, setTouchpointLoadError] = useState<string | null>(null);

    // Media Analysis State
    const [mediaAnalysis, setMediaAnalysis] = useState<any>(null);
    const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);
    const [analysisError, setAnalysisError] = useState<string | null>(null);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [isClearing, setIsClearing] = useState(false);
    const [showSuppressedQuestions, setShowSuppressedQuestions] = useState(false);

    const [touchpointForm, setTouchpointForm] = useState<{
        name: string;
        short_name: string;
        type: string;
        status: string;
        scheduled_at: any;
        started_at: any;
        completed_at: any;
        analysis_url: string;
        advisor_analysis_url: string;
        product_description: string;
        suppressed_questions: string[];
        product_description_section: string;
        default_survey_url: string;
        alchemer_survey_id: string;
        survey_questions_json: any;
        survey_layout_json: any;
        advisor_activity_uuid: string | null;
        highlight_video_url: string;
        advisor_highlight_video_url: string;
        prompt_html: string;
        prompt_image_url: string;
        prompt_video_url: string;
        media_analysis_input_questions?: string[];
    }>({
        name: "",
        short_name: "",
        type: "default",
        status: "draft",
        scheduled_at: null,
        started_at: null,
        completed_at: null,
        analysis_url: "",
        advisor_analysis_url: "",
        product_description: "",
        suppressed_questions: [],
        product_description_section: "",
        default_survey_url: "",
        alchemer_survey_id: "",
        survey_questions_json: { pages: [] },
        survey_layout_json: null,
        advisor_activity_uuid: null,
        highlight_video_url: "",
        advisor_highlight_video_url: "",
        prompt_html: "",
        prompt_image_url: "",
        prompt_video_url: "",
        media_analysis_input_questions: []
    });

    const adminTouchpointClient = ClientProvider.provideAdminTouchpointClient();

    // Load touchpoint data when component mounts or touchpointUuid changes
    useEffect(() => {
        const loadTouchpoint = async () => {
            if (!touchpointUuid) {
                // No touchpoint UUID means this is a new touchpoint
                return;
            }

            setIsLoadingTouchpoint(true);
            setTouchpointLoadError(null);

            try {
                const accessToken = await getAccessTokenSilently();
                const { data, error } = await adminTouchpointClient.getTouchpointByUuid(accessToken, touchpointUuid);

                if (error) {
                    setTouchpointLoadError(error.message || 'Failed to load touchpoint');
                    return;
                }

                if (data?.touchpoint) {
                    const fetchedTouchpoint = data.touchpoint;
                    setTouchpoint(fetchedTouchpoint);
                    // Update form with loaded data
                    setTouchpointForm({
                        name: fetchedTouchpoint.name || "",
                        short_name: fetchedTouchpoint.short_name || "",
                        type: fetchedTouchpoint.type || "default",
                        status: fetchedTouchpoint.status || "draft",
                        scheduled_at: fetchedTouchpoint.scheduled_at ? dayjs(fetchedTouchpoint.scheduled_at) : null,
                        started_at: fetchedTouchpoint.started_at ? dayjs(fetchedTouchpoint.started_at) : null,
                        completed_at: fetchedTouchpoint.completed_at ? dayjs(fetchedTouchpoint.completed_at) : null,
                        analysis_url: fetchedTouchpoint.analysis_url || "",
                        advisor_analysis_url: fetchedTouchpoint.advisor_analysis_url || "",
                        product_description: fetchedTouchpoint.product_description || "",
                        suppressed_questions: fetchedTouchpoint.suppressed_questions || [],
                        product_description_section: fetchedTouchpoint.product_description_section || "",
                        default_survey_url: fetchedTouchpoint.default_survey_url || "",
                        alchemer_survey_id: fetchedTouchpoint.alchemer_survey_id || "",
                        survey_questions_json: fetchedTouchpoint.survey_questions_json || { pages: [] },
                        survey_layout_json: fetchedTouchpoint.survey_layout_json || null,
                        advisor_activity_uuid: fetchedTouchpoint.advisor_activity?.uuid || null,
                        highlight_video_url: fetchedTouchpoint.highlight_video_url || "",
                        advisor_highlight_video_url: fetchedTouchpoint.advisor_highlight_video_url || "",
                        prompt_html: fetchedTouchpoint.prompt_html || "",
                        prompt_image_url: fetchedTouchpoint.prompt_image_url || "",
                        prompt_video_url: fetchedTouchpoint.prompt_video_url || "",
                        media_analysis_input_questions: fetchedTouchpoint.media_analysis_input_questions || []
                    });
                }
            } catch (err) {
                setTouchpointLoadError('Failed to load touchpoint data');
                console.error('Error loading touchpoint:', err);
            } finally {
                setIsLoadingTouchpoint(false);
            }
        };

        loadTouchpoint();
    }, [touchpointUuid, getAccessTokenSilently]);

    // Section management functions
    const createNewSection = () => {
        const layout = touchpointForm.survey_layout_json || getDefaultLayout();
        const newSectionId = `section-${Date.now()}`;
        const newSection = {
            id: newSectionId,
            name: "New Section",
            order: layout.sections.length + 1,
            visible: true,
            questions: [],
            description: null,
            display_options: {
                css_class: `section-${Date.now()}`,
                collapsible: false,
                show_progress: true
            }
        };

        setTouchpointForm(prev => ({
            ...prev,
            survey_layout_json: {
                ...layout,
                sections: [...layout.sections, newSection]
            }
        }));

        setEditingSectionId(newSectionId);
        setSectionDialogOpen(true);
    };

    const updateSection = (sectionId: string, updates: Record<string, any>) => {
        const layout = touchpointForm.survey_layout_json || getDefaultLayout();
        const updatedSections = layout.sections.map((section: any) =>
            section.id === sectionId ? { ...section, ...updates } : section
        );

        setTouchpointForm(prev => ({
            ...prev,
            survey_layout_json: {
                ...layout,
                sections: updatedSections
            }
        }));
    };

    const deleteSection = (sectionId: string) => {
        const layout = touchpointForm.survey_layout_json || getDefaultLayout();
        const filteredSections = layout.sections.filter((section: any) => section.id !== sectionId);

        // Reorder remaining sections
        const reorderedSections = filteredSections.map((section: any, index: number) => ({
            ...section,
            order: index + 1
        }));

        setTouchpointForm(prev => ({
            ...prev,
            survey_layout_json: {
                ...layout,
                sections: reorderedSections
            }
        }));
    };

    const moveSectionUp = (sectionId: string) => {
        const layout = touchpointForm.survey_layout_json || getDefaultLayout();
        const sections = [...layout.sections];
        const sectionIndex = sections.findIndex(s => s.id === sectionId);

        if (sectionIndex > 0) {
            [sections[sectionIndex], sections[sectionIndex - 1]] = [sections[sectionIndex - 1], sections[sectionIndex]];

            // Update order values
            sections.forEach((section, index) => {
                section.order = index + 1;
            });

            setTouchpointForm(prev => ({
                ...prev,
                survey_layout_json: {
                    ...layout,
                    sections
                }
            }));
        }
    };

    const moveSectionDown = (sectionId: string) => {
        const layout = touchpointForm.survey_layout_json || getDefaultLayout();
        const sections = [...layout.sections];
        const sectionIndex = sections.findIndex(s => s.id === sectionId);

        if (sectionIndex < sections.length - 1) {
            [sections[sectionIndex], sections[sectionIndex + 1]] = [sections[sectionIndex + 1], sections[sectionIndex]];

            // Update order values
            sections.forEach((section, index) => {
                section.order = index + 1;
            });

            setTouchpointForm(prev => ({
                ...prev,
                survey_layout_json: {
                    ...layout,
                    sections
                }
            }));
        }
    };

    // Question management functions
    const createDefaultMapping = (type: MappingType): FieldMapping => {
        switch (type) {
            case 'direct':
                return { type: 'direct', default_value: '' };

            case 'option_mapping':
                return {
                    type: 'option_mapping',
                    value_mappings: {},
                    default_value: 'unknown'
                };

            case 'boolean_convert':
                return {
                    type: 'boolean_convert',
                    true_values: ['Yes'],
                    false_values: ['No'],
                    default_value: 'false'
                };

            case 'regex_extract':
                return {
                    type: 'regex_extract',
                    regex_pattern: '',
                    default_value: ''
                };

            case 'numeric_extract':
                return {
                    type: 'numeric_extract',
                    default_value: '0'
                };

            default:
                throw new Error(`Unknown mapping type: ${type}`);
        }
    };

    const updateQuestion = (sectionId: string, questionId: number, updates: Record<string, any>) => {
        const layout = touchpointForm.survey_layout_json;
        const updatedSections = layout.sections.map((section: any) => {
            if (section.id === sectionId) {
                const updatedQuestions = section.questions.map((question: any) =>
                    question.alchemer_question_id === questionId ? { ...question, ...updates } : question
                );
                return { ...section, questions: updatedQuestions };
            }
            return section;
        });

        setTouchpointForm(prev => ({
            ...prev,
            survey_layout_json: {
                ...layout,
                sections: updatedSections
            }
        }));
    };

    const moveQuestionUp = (sectionId: string, questionId: number) => {
        const layout = touchpointForm.survey_layout_json;
        const section = layout.sections.find((s: any) => s.id === sectionId);
        if (!section) return;

        const questions = [...section.questions];
        const questionIndex = questions.findIndex(q => q.alchemer_question_id === questionId);

        if (questionIndex > 0) {
            [questions[questionIndex], questions[questionIndex - 1]] = [questions[questionIndex - 1], questions[questionIndex]];

            // Update order values
            questions.forEach((question, index) => {
                question.order = index + 1;
            });

            updateSection(sectionId, { questions });
        }
    };

    const moveQuestionDown = (sectionId: string, questionId: number) => {
        const layout = touchpointForm.survey_layout_json;
        const section = layout.sections.find((s: any) => s.id === sectionId);
        if (!section) return;

        const questions = [...section.questions];
        const questionIndex = questions.findIndex(q => q.alchemer_question_id === questionId);

        if (questionIndex < questions.length - 1) {
            [questions[questionIndex], questions[questionIndex + 1]] = [questions[questionIndex + 1], questions[questionIndex]];

            // Update order values
            questions.forEach((question, index) => {
                question.order = index + 1;
            });

            updateSection(sectionId, { questions });
        }
    };

    const moveQuestionToSection = (questionId: number, fromSectionId: string, toSectionId: string) => {
        if (fromSectionId === toSectionId) return;

        const layout = touchpointForm.survey_layout_json;
        const fromSection = layout.sections.find((s: any) => s.id === fromSectionId);
        const toSection = layout.sections.find((s: any) => s.id === toSectionId);

        if (!fromSection || !toSection) return;

        // Find and remove the question from the source section
        const questionIndex = fromSection.questions.findIndex((q: any) => q.alchemer_question_id === questionId);
        if (questionIndex === -1) return;

        const questionToMove = fromSection.questions[questionIndex];
        const updatedFromQuestions = fromSection.questions.filter((q: any) => q.alchemer_question_id !== questionId);

        // Reorder remaining questions in source section
        updatedFromQuestions.forEach((question: any, index: number) => {
            question.order = index + 1;
        });

        // Add question to destination section with proper order
        const updatedToQuestions = [...toSection.questions, {
            ...questionToMove,
            order: toSection.questions.length + 1
        }];

        // Update the layout
        const updatedSections = layout.sections.map((section: any) => {
            if (section.id === fromSectionId) {
                return { ...section, questions: updatedFromQuestions };
            } else if (section.id === toSectionId) {
                return { ...section, questions: updatedToQuestions };
            }
            return section;
        });

        setTouchpointForm(prev => ({
            ...prev,
            survey_layout_json: {
                ...layout,
                sections: updatedSections
            }
        }));

        setMoveQuestionMenuOpen(null);
        setMoveQuestionMenuAnchor(null);
    };

    const getDefaultLayout = () => {
        return {
            version: "1.0",
            metadata: {
                alchemer_survey_id: touchpointForm.alchemer_survey_id || "",
                last_sync: new Date().toISOString(),
                layout_created: new Date().toISOString(),
                sync_hash: "",
                created_at: new Date().toISOString(),
                created_by: "admin",
                description: "Default layout"
            },
            sections: [],
            global_settings: {
                show_section_numbers: false,
                show_question_numbers: false,
                allow_back_navigation: true,
                save_progress: true,
                theme: "modern",
                custom_css: null
            },
            suppressed_elements: {
                questions: [],
                pages: [],
                sections: []
            },
            field_mappings: {
                interests: [],
                investment: { amountFields: [] },
                roleFields: [],
                maxSpendFields: [],
                demandFields: []
            }
        };
    };

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        if (name === "suppressed_questions") {
            setTouchpointForm(prev => ({
                ...prev,
                [name]: value.split("\n")
            }));
        } else {
            setTouchpointForm(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleDateChange = (name: string) => (value: any) => {
        setTouchpointForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        const accessToken = await getAccessTokenSilently();

        const formData = {
            ...touchpointForm,
            scheduled_at: touchpointForm.scheduled_at?.toISOString(),
            started_at: touchpointForm.started_at?.toISOString(),
            completed_at: touchpointForm.completed_at?.toISOString()
        };

        if (touchpoint?.uuid) {
            // Update existing touchpoint
            const { data, error } = await adminTouchpointClient.updateTouchpoint(
                accessToken,
                project.id,
                touchpoint.uuid,
                formData
            );

            if (!error) {
                setEditMode(false);
                reloadTouchpoints();
            }
        } else {
            // Create new touchpoint
            const { data, error } = await adminTouchpointClient.createTouchpoint(
                accessToken,
                project.id,
                formData
            );

            if (!error) {
                setEditMode(false);
                reloadTouchpoints();
            }
        }
    };

    const handleCancel = () => {
        setEditMode(false);
    };

    const handleTabChange = (event: any, newValue: number) => {
        setCurrentTab(newValue);
    };

    // Media Analysis Functions
    const loadMediaAnalysis = async (forceRegeneration = false) => {
        if (!touchpoint?.uuid) return;

        setIsLoadingAnalysis(true);
        setAnalysisError(null);

        try {
            const accessToken = await getAccessTokenSilently();
            const { data, error } = await adminTouchpointClient.getMediaResponseAnalysis(
                accessToken,
                touchpoint.uuid,
                forceRegeneration
            );

            if (error) {
                setAnalysisError(error.message || 'Failed to load media analysis');
                return;
            }

            setMediaAnalysis(data?.analysis || null);
        } catch (err) {
            setAnalysisError('Failed to load media analysis');
            console.error('Error loading media analysis:', err);
        } finally {
            setIsLoadingAnalysis(false);
        }
    };

    const regenerateMediaAnalysis = async () => {
        if (!touchpoint?.uuid) return;

        setIsRegenerating(true);
        setAnalysisError(null);

        try {
            const accessToken = await getAccessTokenSilently();
            const { data, error } = await adminTouchpointClient.regenerateMediaAnalysis(
                accessToken,
                touchpoint.uuid
            );

            if (error) {
                setAnalysisError(error.message || 'Failed to regenerate media analysis');
                return;
            }

            setMediaAnalysis(data?.analysis || null);
        } catch (err) {
            setAnalysisError('Failed to regenerate media analysis');
            console.error('Error regenerating media analysis:', err);
        } finally {
            setIsRegenerating(false);
        }
    };

    const clearMediaAnalysis = async () => {
        if (!touchpoint?.uuid) return;

        setIsClearing(true);
        setAnalysisError(null);

        try {
            const accessToken = await getAccessTokenSilently();
            const { error } = await adminTouchpointClient.clearMediaAnalysis(
                accessToken,
                touchpoint.uuid
            );

            if (error) {
                setAnalysisError(error.message || 'Failed to clear media analysis');
                return;
            }

            setMediaAnalysis(null);
        } catch (err) {
            setAnalysisError('Failed to clear media analysis');
            console.error('Error clearing media analysis:', err);
        } finally {
            setIsClearing(false);
        }
    };

    // Load media analysis when touchpoint changes
    useEffect(() => {
        if (touchpoint?.uuid) {
            loadMediaAnalysis();
        }
    }, [touchpoint?.uuid]);

    const reloadSurveyData = async () => {
        setIsReloading(true);
        try {
            const accessToken = await getAccessTokenSilently();
            const { data, error } = await adminTouchpointClient.reloadSurveyData(accessToken, touchpoint.uuid);
            if (!error) {
                const fetchedTouchpoint = data.touchpoint;
                // Ensure the reloaded layout has field_mappings
                const layoutWithDefaults = {
                    ...getDefaultLayout(),
                    ...(fetchedTouchpoint.survey_layout_json || {}),
                    field_mappings: {
                        ...getDefaultLayout().field_mappings,
                        ...(fetchedTouchpoint.survey_layout_json?.field_mappings || {})
                    }
                };

                setTouchpointForm(prev => ({
                    ...prev,
                    survey_questions_json: fetchedTouchpoint.survey_questions_json || { pages: [] },
                    survey_layout_json: layoutWithDefaults
                }));

                // Set results for display
                setReloadResults({
                    success: true,
                    data: fetchedTouchpoint,
                    timestamp: new Date().toISOString(),
                    questionsCount: fetchedTouchpoint.survey_questions_json?.pages?.reduce((total: number, page: any) => {
                        return total + (page.questions?.length || 0);
                    }, 0) || 0,
                    sectionsCount: layoutWithDefaults.sections?.length || 0
                });
                setReloadResultsDialogOpen(true);
            } else {
                setReloadResults({
                    success: false,
                    error: error,
                    timestamp: new Date().toISOString()
                });
                setReloadResultsDialogOpen(true);
            }
        } catch (err) {
            setReloadResults({
                success: false,
                error: err,
                timestamp: new Date().toISOString()
            });
            setReloadResultsDialogOpen(true);
        } finally {
            setIsReloading(false);
        }
    };

    const cleanHtmlTags = (htmlString: string): string => {
        if (typeof htmlString !== 'string') return htmlString;

        // Replace <br>, <br/>, <br /> tags with spaces
        let cleaned = htmlString.replace(/<br\s*\/?>/gi, ' ');

        // Remove all other HTML tags
        cleaned = cleaned.replace(/<[^>]*>/g, '');

        // Clean up multiple spaces and trim
        cleaned = cleaned.replace(/\s+/g, ' ').trim();

        return cleaned;
    };

    const getDisplayTitle = (titleObj: any, fallback: string = "") => {
        let title = '';

        if (typeof titleObj === 'string') {
            title = titleObj;
        } else if (typeof titleObj === 'object' && titleObj !== null) {
            title = titleObj.English || titleObj.english || Object.values(titleObj)[0] || fallback;
        } else {
            title = fallback;
        }

        return cleanHtmlTags(title);
    };

    const getOriginalQuestionTitle = (question: any) => {
        // Find the question in survey_questions_json to get the original title
        const surveyData = touchpointForm.survey_questions_json;
        if (surveyData?.pages) {
            for (const page of surveyData.pages) {
                if (page.questions) {
                    const foundQuestion = page.questions.find((q: any) => q.id === question.alchemer_question_id);
                    if (foundQuestion) {
                        return getDisplayTitle(foundQuestion.title, `Question ${question.alchemer_question_id}`);
                    }
                }
            }
        }

        return `Question ${question.alchemer_question_id}`;
    };

    const getQuestionDisplayInfo = (question: any) => {
        const originalTitle = getOriginalQuestionTitle(question);
        const customTitle = question.custom_title;

        return {
            displayTitle: customTitle || originalTitle,
            originalTitle,
            hasCustomTitle: !!customTitle
        };
    };

    const renderGeneralTab = () => (
        <Box>
            <Box sx={{ display: "flex", gap: 2, flexDirection: "row", mt: 2 }}>

                <TextField
                    sx={{ width: "70%" }}
                    name="name"
                    label="Name"
                    value={touchpointForm.name}
                    onChange={handleChange}
                    required
                />
                <TextField
                    sx={{ width: "30%" }}
                    name="short_name"
                    label="Short Name"
                    value={touchpointForm.short_name}
                    onChange={handleChange}
                />
            </Box>

            <Box sx={{ display: "flex", gap: 2, flexDirection: "row", mt: 2 }}>
                <TextField
                    fullWidth
                    name="alchemer_survey_id"
                    label="Alchemer Survey ID"
                    value={touchpointForm.alchemer_survey_id}
                    onChange={handleChange}
                />
                <TextField
                    fullWidth
                    name="default_survey_url"
                    label="Alchemer Survey URL"
                    value={touchpointForm.default_survey_url}
                    onChange={handleChange}
                />
            </Box>

            <Box sx={{ display: "flex", gap: 2, flexDirection: "row", mt: 2 }}>
                <FormControl fullWidth>
                    <InputLabel size="small">Type</InputLabel>
                    <Select
                        name="type"
                        value={touchpointForm.type}
                        onChange={handleChange}
                        label="Type"
                        required
                        size="small"
                    >
                        {touchpointTypes.map(type => (
                            <MenuItem key={type.value} value={type.value}>
                                {type.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl fullWidth>
                    <InputLabel size="small">Status</InputLabel>
                    <Select
                        name="status"
                        value={touchpointForm.status}
                        onChange={handleChange}
                        label="Status"
                        required
                        size="small"
                    >
                        {touchpointStatuses.map(status => (
                            <MenuItem key={status.value} value={status.value}>
                                {status.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl fullWidth>
                    <InputLabel size="small">Advisor Shares</InputLabel>
                    <Select
                        name="advisor_activity_uuid"
                        value={touchpointForm.advisor_activity_uuid || ""}
                        onChange={handleChange}
                        label="Advisor Activity"
                        required
                        size="small"
                    >
                        {availableActivities.map((activity: IAdvisorActivity) => (
                            <MenuItem key={activity.uuid} value={activity.uuid}>
                                {activity.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Box sx={{ display: "flex", gap: 2, flexDirection: "row", mt: 2 }}>
                <DateTimePicker
                    label="Scheduled For"
                    value={touchpointForm.scheduled_at}
                    onChange={handleDateChange("scheduled_at")}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            size: "small"
                        }
                    }}
                />

                <DateTimePicker
                    label="Started At"
                    value={touchpointForm.started_at}
                    onChange={handleDateChange("started_at")}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            size: "small"
                        }
                    }}
                />

                <DateTimePicker
                    label="Completed At"
                    value={touchpointForm.completed_at}
                    onChange={handleDateChange("completed_at")}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            size: "small"
                        }
                    }}
                />
            </Box>

            <Divider sx={{ mt: 2 }} />

            <TextField
                fullWidth
                name="product_description_section"
                label="Product Description Section"
                value={touchpointForm.product_description_section}
                onChange={handleChange}
                sx={{ mt: 2 }}
            />

            <TextField
                fullWidth
                name="product_description"
                label="Product Description"
                value={touchpointForm.product_description}
                onChange={handleChange}
                multiline
                rows={4}
                sx={{ mt: 2 }}
            />

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <TextField
                    fullWidth
                    name="analysis_url"
                    label="Analysis URL"
                    value={touchpointForm.analysis_url}
                    onChange={handleChange}
                    sx={{ mt: 2 }}
                />

                <TextField
                    fullWidth
                    name="advisor_analysis_url"
                    label="Advisor Analysis URL"
                    value={touchpointForm.advisor_analysis_url}
                    onChange={handleChange}
                    sx={{ mt: 2 }}
                />
            </Stack>

            <Divider sx={{ mt: 2 }} />

            <TextField
                fullWidth
                name="prompt_html"
                label="AdvisorPrompt HTML"
                value={touchpointForm.prompt_html || ""}
                onChange={handleChange}
                sx={{ mt: 2 }}
                multiline
                rows={2}
            />

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <TextField
                    fullWidth
                    name="prompt_image_url"
                    label="Prompt Image URL"
                    value={touchpointForm.prompt_image_url || ""}
                    onChange={handleChange}
                />

                <TextField
                    fullWidth
                    name="prompt_video_url"
                    label="Prompt Video URL"
                    value={touchpointForm.prompt_video_url || ""}
                    onChange={handleChange}
                />
            </Stack>

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <TextField
                    fullWidth
                    name="highlight_video_url"
                    label="Highlight Video URL"
                    value={touchpointForm.highlight_video_url || ""}
                    onChange={handleChange}
                    sx={{ mt: 2 }}
                />

                <TextField
                    fullWidth
                    name="advisor_highlight_video_url"
                    label="Advisor Highlight Video URL"
                    value={touchpointForm.advisor_highlight_video_url || ""}
                    onChange={handleChange}
                    sx={{ mt: 2 }}
                />
            </Stack>

            <Typography
                variant="body2"
                color="primary"
                sx={{
                    mt: 2,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    '&:hover': {
                        textDecoration: 'none'
                    }
                }}
                onClick={() => {
                    setShowSuppressedQuestions(!showSuppressedQuestions);
                }}
            >
                Suppressed Questions (legacy)
            </Typography>

            {showSuppressedQuestions && (
                <TextField
                    fullWidth
                    name="suppressed_questions"
                    label="Suppressed Questions"
                    value={touchpointForm.suppressed_questions.join("\n")}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    sx={{ mt: 2 }}
                />
            )}
        </Box>
    );

    const renderLayoutTab = () => (
        <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
                Survey Layout Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure sections and questions for your survey layout. You can create sections, reorder questions, and customize display options.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Button
                    variant="contained"
                    onClick={() => reloadSurveyData()}
                    disabled={isReloading}
                >
                    {isReloading ? "Reloading..." : "Reload Survey Data"}
                </Button>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={createNewSection}>
                    Add Section
                </Button>
            </Box>

            {touchpointForm.survey_layout_json?.sections && touchpointForm.survey_layout_json.sections.length > 0 ? (
                touchpointForm.survey_layout_json.sections
                    .sort((a: any, b: any) => a.order - b.order)
                    .map((section: any) => (
                        <Accordion key={section.id} defaultExpanded={section.visible} sx={{ mb: 2, border: "1px solid #e0e0e0", borderRadius: "8px", opacity: section.visible ? "1" : "0.5" }}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    '&:hover .section-actions': {
                                        opacity: 1,
                                        visibility: 'visible'
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: "700" }}>
                                            {section.name}
                                        </Typography>
                                        {false && (
                                            <>
                                                <Chip
                                                    label={`Order: ${section.order}`}
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                />
                                                <Chip
                                                    label={`${section.questions?.length || 0} questions`}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                                <Chip
                                                    label={section.visible ? "Visible" : "Hidden"}
                                                    size="small"
                                                    color={section.visible ? "success" : "default"}
                                                    variant="outlined"
                                                />
                                            </>
                                        )}
                                    </Box>
                                    <Box
                                        className="section-actions"
                                        sx={{
                                            display: 'flex',
                                            gap: 1,
                                            opacity: 0,
                                            visibility: 'hidden',
                                            transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out'
                                        }}
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                moveSectionUp(section.id);
                                            }}
                                            disabled={section.order === 1}
                                        >
                                            <ArrowUpwardIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                moveSectionDown(section.id);
                                            }}
                                            disabled={section.order === touchpointForm.survey_layout_json.sections.length}
                                        >
                                            <ArrowDownwardIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                updateSection(section.id, { visible: !section.visible });
                                            }}
                                            color={section.visible ? "primary" : "default"}
                                        >
                                            {section.visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setEditingSectionId(section.id);
                                                setSectionDialogOpen(true);
                                            }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (window.confirm(`Are you sure you want to delete the section "${section.name}"?`)) {
                                                    deleteSection(section.id);
                                                }
                                            }}
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {section.questions && section.questions.length > 0 ? (
                                    <Box sx={{ pl: 2 }}>
                                        {section.questions
                                            .sort((a: any, b: any) => a.order - b.order)
                                            .map((question: any) => (
                                                <Box
                                                    key={question.alchemer_question_id}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        py: 2,
                                                        borderBottom: '1px solid #e0e0e0',
                                                        '&:hover .question-actions': {
                                                            opacity: 1,
                                                            visibility: 'visible'
                                                        },
                                                        opacity: question.visible ? "1" : "0.3"
                                                    }}
                                                >
                                                    <Box sx={{ flex: 1 }}>
                                                        {(() => {
                                                            const { displayTitle, originalTitle, hasCustomTitle } = getQuestionDisplayInfo(question);
                                                            return (
                                                                <>
                                                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                                        {displayTitle}
                                                                    </Typography>
                                                                    {hasCustomTitle && (
                                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                                                                            Original: {originalTitle}
                                                                        </Typography>
                                                                    )}
                                                                </>
                                                            );
                                                        })()}
                                                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                            <Chip
                                                                label={`ID: ${question.alchemer_question_id}`}
                                                                size="small"
                                                                variant="outlined"
                                                                color="primary"
                                                            />
                                                            {false && question.alchemer_page_id && (
                                                                <Chip
                                                                    label={`Page: ${question.alchemer_page_id}`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="secondary"
                                                                />
                                                            )}
                                                            {false && (
                                                                <Chip
                                                                    label={`Order: ${question.order}`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                            {question.required && (
                                                                <Chip
                                                                    label="Required"
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="error"
                                                                />
                                                            )}
                                                            <Chip
                                                                label={question.visible ? "Visible" : "Hidden"}
                                                                size="small"
                                                                color={question.visible ? "success" : "default"}
                                                                variant="outlined"
                                                            />
                                                        </Box>
                                                        {question.custom_description && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                                {question.custom_description}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Box
                                                        className="question-actions"
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 1,
                                                            opacity: 0,
                                                            visibility: 'hidden',
                                                            transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out'
                                                        }}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => moveQuestionUp(section.id, question.alchemer_question_id)}
                                                            disabled={question.order === 1}
                                                            title="Move up"
                                                        >
                                                            <ArrowUpwardIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => moveQuestionDown(section.id, question.alchemer_question_id)}
                                                            disabled={question.order === section.questions.length}
                                                            title="Move down"
                                                        >
                                                            <ArrowDownwardIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                setMoveQuestionMenuOpen(`${section.id}-${question.alchemer_question_id}`);
                                                                setMoveQuestionMenuAnchor(e.currentTarget);
                                                            }}
                                                            disabled={touchpointForm.survey_layout_json?.sections?.length <= 1}
                                                            title="Move to section"
                                                        >
                                                            <MoveDownIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => updateQuestion(section.id, question.alchemer_question_id, { visible: !question.visible })}
                                                            color={question.visible ? "primary" : "default"}
                                                            title="Toggle visibility"
                                                        >
                                                            {question.visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setEditingQuestion(question);
                                                                setEditingQuestionSectionId(section.id);
                                                                setQuestionDialogOpen(true);
                                                            }}
                                                            title="Edit question"
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Box>

                                                    {/* Move Question Menu */}
                                                    <Menu
                                                        open={moveQuestionMenuOpen === `${section.id}-${question.alchemer_question_id}`}
                                                        onClose={() => {
                                                            setMoveQuestionMenuOpen(null);
                                                            setMoveQuestionMenuAnchor(null);
                                                        }}
                                                        anchorEl={moveQuestionMenuAnchor}
                                                        anchorOrigin={{
                                                            vertical: 'bottom',
                                                            horizontal: 'left',
                                                        }}
                                                    >
                                                        <MenuItem disabled>
                                                            <Typography variant="subtitle2">Move to section:</Typography>
                                                        </MenuItem>
                                                        {touchpointForm.survey_layout_json?.sections
                                                            ?.filter((s: any) => s.id !== section.id)
                                                            ?.map((targetSection: any) => (
                                                                <MenuItem
                                                                    key={targetSection.id}
                                                                    onClick={() => moveQuestionToSection(question.alchemer_question_id, section.id, targetSection.id)}
                                                                >
                                                                    {targetSection.name}
                                                                </MenuItem>
                                                            ))}
                                                    </Menu>
                                                </Box>
                                            ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No questions in this section.
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    ))
            ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        No sections configured.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Click "Reload Survey Data" to fetch the latest survey structure or "Add Section" to create a new section.
                    </Typography>
                </Box>
            )}

            {/* Section Edit Dialog */}
            <SectionEditDialog
                open={sectionDialogOpen}
                onClose={() => {
                    setSectionDialogOpen(false);
                    setEditingSectionId(null);
                }}
                section={editingSectionId ? touchpointForm.survey_layout_json?.sections?.find((s: any) => s.id === editingSectionId) : null}
                onSave={(updates) => {
                    if (editingSectionId) {
                        updateSection(editingSectionId, updates);
                    }
                    setSectionDialogOpen(false);
                    setEditingSectionId(null);
                }}
            />

            {/* Question Edit Dialog */}
            <QuestionEditDialog
                open={questionDialogOpen}
                onClose={() => {
                    setQuestionDialogOpen(false);
                    setEditingQuestion(null);
                    setEditingQuestionSectionId(null);
                }}
                question={editingQuestion}
                onSave={(updates) => {
                    if (editingQuestion && editingQuestionSectionId) {
                        updateQuestion(editingQuestionSectionId, editingQuestion.alchemer_question_id, updates);
                    }
                    setQuestionDialogOpen(false);
                    setEditingQuestion(null);
                    setEditingQuestionSectionId(null);
                }}
            />

            {/* Reload Results Dialog */}
            <ReloadResultsDialog
                open={reloadResultsDialogOpen}
                onClose={() => setReloadResultsDialogOpen(false)}
                results={reloadResults}
            />
        </Box>
    );

    const renderMappingTab = () => {
        // Get all available questions from survey data
        const availableQuestions: Array<{ id: string, title: string, options?: string[] }> = [];

        if (touchpointForm.survey_questions_json?.pages) {
            touchpointForm.survey_questions_json.pages.forEach((page: any) => {
                if (page.questions) {
                    page.questions.forEach((question: any) => {
                        if (question.type === 'HTTP') {
                            return;
                        }
                        const questionTitle = getDisplayTitle(question.title, `Question ${question.id}`);
                        const options = question.options?.map((opt: any) =>
                            getDisplayTitle(opt.title, opt.value || 'Option')
                        ) || [];

                        availableQuestions.push({
                            id: question.id.toString(),
                            title: questionTitle,
                            options: options.length > 0 ? options : undefined
                        });
                    });
                }
            });
        }

        const getCurrentFieldMappings = () => {
            const layout = touchpointForm.survey_layout_json || getDefaultLayout();
            return layout.field_mappings || {
                interests: [],
                investment: { amountFields: [] },
                roleFields: [],
                maxSpendFields: [],
                demandFields: []
            };
        };

        const updateFieldMappings = (updates: any) => {
            const layout = touchpointForm.survey_layout_json || getDefaultLayout();
            const updatedLayout = {
                ...layout,
                field_mappings: { ...getCurrentFieldMappings(), ...updates }
            };

            setTouchpointForm(prev => ({
                ...prev,
                survey_layout_json: updatedLayout
            }));
        };

        // Predefined system variables
        const systemVariables = [
            { key: 'demoInterest', label: 'Demo Interest', description: 'Interest in seeing product demonstrations' },
            { key: 'betaInterest', label: 'Beta Interest', description: 'Interest in beta testing or trial access' },
            { key: 'investorInterest', label: 'Investor Interest', description: 'Interest in investment opportunities' },
            { key: 'advisingInterest', label: 'Advising Interest', description: 'Interest in advisory roles or consulting' },
            { key: 'employmentInterest', label: 'Employment Interest', description: 'Interest in job opportunities' },
            { key: 'followupInterest', label: 'Follow-up Interest', description: 'Interest in general follow-up conversations' }
        ];

        const getInterestMapping = (key: string) => {
            const currentMappings = getCurrentFieldMappings();
            return currentMappings.interests.find((interest: any) => interest.key === key) || { key, fields: [] };
        };

        const addFieldToInterest = (interestKey: string, questionNum: string, value: string) => {
            const currentMappings = getCurrentFieldMappings();
            const updatedInterests = [...currentMappings.interests];

            // Find existing interest or create new one
            let interestIndex = updatedInterests.findIndex((interest: any) => interest.key === interestKey);
            if (interestIndex === -1) {
                updatedInterests.push({ key: interestKey, fields: [] });
                interestIndex = updatedInterests.length - 1;
            }

            // Add the new field mapping
            updatedInterests[interestIndex].fields.push({ questionNum, value });
            updateFieldMappings({ interests: updatedInterests });
        };

        const removeFieldFromInterest = (interestKey: string, fieldIndex: number) => {
            const currentMappings = getCurrentFieldMappings();
            const updatedInterests = [...currentMappings.interests];

            const interestIndex = updatedInterests.findIndex((interest: any) => interest.key === interestKey);
            if (interestIndex !== -1) {
                updatedInterests[interestIndex].fields.splice(fieldIndex, 1);

                // Remove the entire interest if no fields left
                if (updatedInterests[interestIndex].fields.length === 0) {
                    updatedInterests.splice(interestIndex, 1);
                }

                updateFieldMappings({ interests: updatedInterests });
            }
        };

        return (
            <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Field Mappings
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Map survey responses to system variables for automated data processing.
                </Typography>

                {/* Market Demand Fields */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Purchasing Interest</Typography>

                    <SimpleFieldMapper
                        label="Demand/Likelihood Questions"
                        fields={getCurrentFieldMappings().demandFields}
                        availableQuestions={availableQuestions}
                        onChange={(demandFields) => updateFieldMappings({ demandFields })}
                        allowValueMapping={false}
                    />
                </Paper>

                {/* Decision Making & Budget Fields */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Role and Spend</Typography>

                    {/* Role Fields with Complex Mapping */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                            Role in the buying decision
                        </Typography>

                        <SimpleFieldMapper
                            label="Role Questions"
                            fields={getCurrentFieldMappings().roleFields}
                            availableQuestions={availableQuestions}
                            onChange={(roleFields) => updateFieldMappings({ roleFields })}
                            allowValueMapping={true}
                        />
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Budget/Spend Fields */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                            Max Spend
                        </Typography>

                        <SimpleFieldMapper
                            label="Budget Questions"
                            fields={getCurrentFieldMappings().maxSpendFields}
                            availableQuestions={availableQuestions}
                            onChange={(maxSpendFields) => updateFieldMappings({ maxSpendFields })}
                            allowValueMapping={false}
                        />
                    </Box>
                </Paper>

                {/* Interest Types Section */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Interest Types</Typography>

                    {systemVariables.map((variable) => {
                        const interestMapping = getInterestMapping(variable.key);
                        return (
                            <Accordion key={variable.key} sx={{ mb: 2 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                {variable.label}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={`${interestMapping.fields.length} mappings`}
                                            size="small"
                                            variant="outlined"
                                            color={interestMapping.fields.length > 0 ? "primary" : "default"}
                                        />
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {/* Current mappings */}
                                        {interestMapping.fields.length > 0 && (
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Current Mappings:</Typography>
                                                {interestMapping.fields.map((field: any, fieldIndex: number) => (
                                                    <Box key={fieldIndex} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 1 }}>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="body2">
                                                                <strong>Question {field.questionNum}:</strong> "{field.value}"
                                                            </Typography>
                                                        </Box>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => removeFieldFromInterest(variable.key, fieldIndex)}
                                                            color="error"
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}

                                        {/* Add new mapping */}
                                        <AddFieldMappingForm
                                            availableQuestions={availableQuestions}
                                            onAdd={(questionNum: string, value: string) => addFieldToInterest(variable.key, questionNum, value)}
                                            label={`Add mapping for ${variable.label}`}
                                        />
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </Paper>

                {/* Investment Amount Fields */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Investment Amount</Typography>

                    <SimpleFieldMapper
                        label="Investment Amount Questions"
                        fields={getCurrentFieldMappings().investment?.amountFields || []}
                        availableQuestions={availableQuestions}
                        onChange={(amountFields) => updateFieldMappings({
                            investment: {
                                ...getCurrentFieldMappings().investment,
                                amountFields
                            }
                        })}
                        allowValueMapping={false}
                    />
                </Paper>
            </Box>
        );
    };

    const renderMediaAnalysisTab = () => {
        const formatTimestamp = (timestamp: string) => {
            return new Date(timestamp).toLocaleString();
        };

        // Get all available questions for media analysis input selection
        const availableQuestions: Array<{ id: string, title: string }> = [];

        if (touchpointForm.survey_questions_json?.pages) {
            touchpointForm.survey_questions_json.pages.forEach((page: any) => {
                if (page.questions) {
                    page.questions.forEach((question: any) => {
                        if (question.type === 'HTTP') {
                            return;
                        }
                        const questionTitle = getDisplayTitle(question.title, `Question ${question.id}`);
                        availableQuestions.push({
                            id: question.id.toString(),
                            title: questionTitle
                        });
                    });
                }
            });
        }

        const currentInputQuestions = touchpointForm.media_analysis_input_questions || [];

        const handleInputQuestionsChange = (selectedQuestions: string[]) => {
            setTouchpointForm(prev => ({
                ...prev,
                media_analysis_input_questions: selectedQuestions
            }));
        };

        return (
            <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Media Response Analysis
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Analyze video and audio transcripts to extract insights, highlights, and topics from media responses.
                </Typography>

                {/* Media Analysis Input Configuration */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Input Questions Configuration</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select which questions should be treated as inputs for media analysis. These questions will be used to identify media responses that should be analyzed.
                    </Typography>

                    <FormControl fullWidth>
                        <InputLabel>Media Analysis Input Questions</InputLabel>
                        <Select
                            multiple
                            value={currentInputQuestions}
                            onChange={(e) => {
                                const selectedValues = typeof e.target.value === 'string'
                                    ? e.target.value.split(',')
                                    : e.target.value;
                                handleInputQuestionsChange(selectedValues);
                            }}
                            label="Media Analysis Input Questions"
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {(selected as string[]).map((questionId) => {
                                        const question = availableQuestions.find(q => q.id === questionId);
                                        return (
                                            <Chip
                                                key={questionId}
                                                label={question ? `${questionId}: ${question.title}` : questionId}
                                                size="small"
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                        >
                            {availableQuestions.map((question) => (
                                <MenuItem key={question.id} value={question.id}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            Question {question.id}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {question.title}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {currentInputQuestions.length > 0 && (
                        <Box sx={{ mt: 2, p: 2, backgroundColor: '#f0f9ff', borderRadius: 1, border: '1px solid #0ea5e9' }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                Selected Input Questions ({currentInputQuestions.length}):
                            </Typography>
                            {currentInputQuestions.map((questionId) => {
                                const question = availableQuestions.find(q => q.id === questionId);
                                return (
                                    <Typography key={questionId} variant="body2" sx={{ mb: 0.5 }}>
                                        • <strong>Q{questionId}:</strong> {question ? question.title : 'Unknown Question'}
                                    </Typography>
                                );
                            })}
                        </Box>
                    )}
                </Paper>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <Button
                        variant="contained"
                        onClick={() => loadMediaAnalysis(false)}
                        disabled={isLoadingAnalysis || !touchpoint?.uuid}
                        startIcon={isLoadingAnalysis ? <CircularProgress size={16} /> : null}
                    >
                        {isLoadingAnalysis ? "Loading..." : "Load Analysis"}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => loadMediaAnalysis(true)}
                        disabled={isLoadingAnalysis || !touchpoint?.uuid}
                        startIcon={isLoadingAnalysis ? <CircularProgress size={16} /> : null}
                    >
                        {isLoadingAnalysis ? "Loading..." : "Force Refresh"}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={regenerateMediaAnalysis}
                        disabled={isRegenerating || !touchpoint?.uuid}
                        startIcon={isRegenerating ? <CircularProgress size={16} /> : null}
                        color="warning"
                    >
                        {isRegenerating ? "Regenerating..." : "Regenerate Analysis"}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={clearMediaAnalysis}
                        disabled={isClearing || !touchpoint?.uuid}
                        startIcon={isClearing ? <CircularProgress size={16} /> : null}
                        color="error"
                    >
                        {isClearing ? "Clearing..." : "Clear Cache"}
                    </Button>
                </Box>

                {/* Workflow Instructions */}
                {currentInputQuestions.length > 0 && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                        <Typography variant="body2">
                            <strong>Workflow:</strong> After updating the input questions above, click "Update Touchpoint" to save the configuration, then click "Regenerate Analysis" to re-analyze with the new input questions.
                        </Typography>
                    </Alert>
                )}

                {/* Error Display */}
                {analysisError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {analysisError}
                    </Alert>
                )}

                {/* Analysis Results */}
                {mediaAnalysis ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Analysis Metadata */}
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Analysis Information</Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Chip
                                    label={`Generated: ${formatTimestamp(mediaAnalysis.generatedAt)}`}
                                    variant="outlined"
                                    color="primary"
                                />
                                <Chip
                                    label={`Version: ${mediaAnalysis.version}`}
                                    variant="outlined"
                                />
                                <Chip
                                    label={mediaAnalysis.isCached ? "Cached" : "Fresh Analysis"}
                                    variant="outlined"
                                    color={mediaAnalysis.isCached ? "success" : "warning"}
                                />
                            </Box>
                        </Paper>

                        {/* Summary */}
                        {mediaAnalysis.summary && (
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>Key Insights</Typography>
                                <Box
                                    sx={{
                                        p: 2,
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: 1,
                                        border: '1px solid #e9ecef'
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: mediaAnalysis.summary.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                    }}
                                />
                            </Paper>
                        )}

                        {/* Topics */}
                        {mediaAnalysis.topics && mediaAnalysis.topics.length > 0 && (
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>Identified Topics</Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {mediaAnalysis.topics.map((topic: string, index: number) => {
                                        const mediaCount = mediaAnalysis.topicToMediaMap?.[topic]?.length || 0;
                                        return (
                                            <Chip
                                                key={index}
                                                label={`${topic} (${mediaCount} responses)`}
                                                variant="outlined"
                                                color="primary"
                                            />
                                        );
                                    })}
                                </Box>
                            </Paper>
                        )}

                        {/* Highlights */}
                        {mediaAnalysis.highlights && mediaAnalysis.highlights.length > 0 && (
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>Key Highlights</Typography>
                                <List>
                                    {mediaAnalysis.highlights.map((highlight: any, index: number) => (
                                        <ListItem key={index} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                            <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 1 }}>
                                                "{highlight.quote}"
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Media Response ID: {highlight.mediaResponseId}
                                            </Typography>
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                        )}

                        {/* Topic to Media Mapping */}
                        {mediaAnalysis.topicToMediaMap && Object.keys(mediaAnalysis.topicToMediaMap).length > 0 && (
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>Topic Coverage</Typography>
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography>View which media responses discuss each topic</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {Object.entries(mediaAnalysis.topicToMediaMap).map(([topic, mediaIds]: [string, any]) => (
                                                <Box key={topic} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                        {topic}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Discussed in {mediaIds.length} media response{mediaIds.length !== 1 ? 's' : ''}:
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                                        {mediaIds.map((id: number) => (
                                                            <Chip
                                                                key={id}
                                                                label={`ID: ${id}`}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        ))}
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            </Paper>
                        )}
                    </Box>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            {isLoadingAnalysis ? "Loading media analysis..." : "No media analysis available."}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {!isLoadingAnalysis && "Click 'Load Analysis' to analyze media responses for this touchpoint."}
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    // Reload Results Dialog Component
    const ReloadResultsDialog = ({ open, onClose, results }: { open: boolean; onClose: () => void; results: any }) => {
        if (!results) return null;

        const formatTimestamp = (timestamp: string) => {
            return new Date(timestamp).toLocaleString();
        };

        const renderSuccessResults = () => (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
                    <Typography variant="h6" color="success.main">
                        ✅ Survey Data Reloaded Successfully
                    </Typography>
                </Box>

                <Box sx={{ p: 2, backgroundColor: '#f0f9ff', borderRadius: 1, border: '1px solid #0ea5e9' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Summary:
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                        • <strong>Questions loaded:</strong> {results.questionsCount}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                        • <strong>Sections configured:</strong> {results.sectionsCount}
                    </Typography>
                    <Typography variant="body2">
                        • <strong>Survey ID:</strong> {results.data?.survey_layout_json?.metadata?.alchemer_survey_id || 'N/A'}
                    </Typography>
                </Box>

                {results.data?.survey_questions_json?.pages && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Pages & Questions:
                        </Typography>
                        <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                            {results.data.survey_questions_json.pages.map((page: any, pageIndex: number) => (
                                <Box key={pageIndex} sx={{ mb: 2, p: 1, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                        Page {page.id || pageIndex + 1} ({page.questions?.length || 0} questions)
                                    </Typography>
                                    {page.questions && page.questions.map((question: any, qIndex: number) => (
                                        <Typography key={qIndex} variant="caption" sx={{ display: 'block', ml: 2, mt: 0.5 }}>
                                            • Q{question.id}: {typeof question.title === 'string' ? question.title : (question.title?.English || question.title?.english || 'Question Title')}
                                        </Typography>
                                    ))}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
            </Box>
        );

        const renderErrorResults = () => (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
                    <Typography variant="h6" color="error.main">
                        ❌ Failed to Reload Survey Data
                    </Typography>
                </Box>

                <Box sx={{ p: 2, backgroundColor: '#fef2f2', borderRadius: 1, border: '1px solid #f87171' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'error.main' }}>
                        Error Details:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                        {typeof results.error === 'string' ? results.error : JSON.stringify(results.error, null, 2)}
                    </Typography>
                </Box>
            </Box>
        );

        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Survey Data Reload Results
                    <Typography variant="caption" color="text.secondary">
                        {formatTimestamp(results.timestamp)}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {results.success ? renderSuccessResults() : renderErrorResults()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    // Show loading state while fetching touchpoint data
    if (isLoadingTouchpoint) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <Typography>Loading touchpoint data...</Typography>
            </Box>
        );
    }

    // Show error state if failed to load touchpoint
    if (touchpointLoadError) {
        return (
            <Box sx={{ p: 2, backgroundColor: '#fef2f2', border: '1px solid #f87171', borderRadius: 1 }}>
                <Typography variant="h6" color="error.main">
                    Error Loading Touchpoint
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                    {touchpointLoadError}
                </Typography>
                <Button variant="outlined" onClick={handleCancel} sx={{ mt: 2 }}>
                    Go Back
                </Button>
            </Box>
        );
    }

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                padding: 2,
                backgroundColor: "#f5f5f5",
            }}
        >
            <ExecTabs value={currentTab} onChange={handleTabChange} sx={{ marginBottom: 0 }}>
                <Tab label="General" />
                <Tab label="Layout" />
                <Tab label="Mapping" />
                <Tab label="Media Analysis" />
            </ExecTabs>

            <Box sx={{ mt: 2 }}>
                {currentTab === 0 && renderGeneralTab()}
                {currentTab === 1 && renderLayoutTab()}
                {currentTab === 2 && renderMappingTab()}
                {currentTab === 3 && renderMediaAnalysisTab()}
            </Box>

            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}>
                <Button variant="outlined" onClick={handleCancel}>
                    Cancel
                </Button>
                <Button variant="contained" type="submit">
                    {touchpoint?.uuid ? "Update" : "Create"} Touchpoint
                </Button>
            </Box>
        </Box>
    );
};

// Section Edit Dialog Component
const SectionEditDialog = ({ open, onClose, section, onSave }: { open: boolean; onClose: () => void; section: any; onSave: (updates: Record<string, any>) => void }) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        visible: true,
        collapsible: false,
        show_progress: true,
        css_class: ''
    });

    useEffect(() => {
        if (section) {
            setFormData({
                name: section.name || '',
                description: section.description || '',
                visible: section.visible !== false,
                collapsible: section.display_options?.collapsible || false,
                show_progress: section.display_options?.show_progress !== false,
                css_class: section.display_options?.css_class || ''
            });
        }
    }, [section]);

    const handleSave = () => {
        onSave({
            name: formData.name,
            description: formData.description || null,
            visible: formData.visible,
            display_options: {
                ...section?.display_options,
                collapsible: formData.collapsible,
                show_progress: formData.show_progress,
                css_class: formData.css_class
            }
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Section</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField
                        fullWidth
                        label="Section Name"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        required
                    />
                    <TextField
                        fullWidth
                        label="Description"
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        multiline
                        rows={2}
                    />
                    <TextField
                        fullWidth
                        label="CSS Class"
                        value={formData.css_class}
                        onChange={(e) => setFormData(prev => ({ ...prev, css_class: e.target.value }))}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.visible}
                                onChange={(e) => setFormData(prev => ({ ...prev, visible: e.target.checked }))}
                            />
                        }
                        label="Visible"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.collapsible}
                                onChange={(e) => setFormData(prev => ({ ...prev, collapsible: e.target.checked }))}
                            />
                        }
                        label="Collapsible"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.show_progress}
                                onChange={(e) => setFormData(prev => ({ ...prev, show_progress: e.target.checked }))}
                            />
                        }
                        label="Show Progress"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained">Save</Button>
            </DialogActions>
        </Dialog>
    );
};

// Helper Components for Mapping Tab

const AddFieldMappingForm = ({ availableQuestions, onAdd, label }: {
    availableQuestions: Array<{ id: string, title: string, options?: string[] }>;
    onAdd: (questionNum: string, value: string) => void;
    label?: string;
}) => {
    const [selectedQuestion, setSelectedQuestion] = useState('');
    const [selectedValue, setSelectedValue] = useState('');
    const [customValue, setCustomValue] = useState('');

    const selectedQuestionData = availableQuestions.find(q => q.id === selectedQuestion);
    const hasOptions = selectedQuestionData?.options && selectedQuestionData.options.length > 0;

    const handleAdd = () => {
        if (selectedQuestion && (selectedValue || customValue)) {
            onAdd(selectedQuestion, customValue || selectedValue);
            setSelectedQuestion('');
            setSelectedValue('');
            setCustomValue('');
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, border: '1px dashed #ccc', borderRadius: 1 }}>
            <Typography variant="subtitle2">{label || "Add New Question Mapping"}</Typography>

            <FormControl fullWidth>
                <InputLabel>Question</InputLabel>
                <Select
                    value={selectedQuestion}
                    label="Question"
                    onChange={(e) => {
                        setSelectedQuestion(e.target.value);
                        setSelectedValue('');
                        setCustomValue('');
                    }}
                >
                    {availableQuestions.map(question => (
                        <MenuItem key={question.id} value={question.id}>
                            {question.id}: {question.title}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {selectedQuestion && (
                <>
                    {hasOptions ? (
                        <FormControl fullWidth>
                            <InputLabel>Answer Option</InputLabel>
                            <Select
                                value={selectedValue}
                                label="Answer Option"
                                onChange={(e) => setSelectedValue(e.target.value)}
                            >
                                {selectedQuestionData!.options!.map((option, index) => (
                                    <MenuItem key={index} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <TextField
                            fullWidth
                            label="Answer Value/Pattern"
                            value={customValue}
                            onChange={(e) => setCustomValue(e.target.value)}
                            placeholder="Enter the answer value that indicates this interest..."
                        />
                    )}

                    <Button
                        variant="outlined"
                        onClick={handleAdd}
                        disabled={!selectedQuestion || (!selectedValue && !customValue)}
                    >
                        Add Mapping
                    </Button>
                </>
            )}
        </Box>
    );
};

const SimpleFieldMapper = ({
    label,
    fields,
    availableQuestions,
    onChange,
    allowValueMapping
}: {
    label: string;
    fields: any[];
    availableQuestions: Array<{ id: string, title: string, options?: string[] }>;
    onChange: (fields: any[]) => void;
    allowValueMapping: boolean;
}) => {
    const [selectedQuestion, setSelectedQuestion] = useState('');
    const [showAddForm, setShowAddForm] = useState(fields.length === 0);

    const addField = () => {
        if (selectedQuestion) {
            if (allowValueMapping) {
                // For complex mappings like role fields with new structure
                const newField = {
                    questionNum: selectedQuestion,
                    values: {
                        Decide: [],
                        Recommend: [],
                        Evaluate: [],
                        Finance: [],
                        Implement: [],
                        None: []
                    }
                };
                onChange([...fields, newField]);
            } else {
                // For simple question number arrays
                if (!fields.includes(selectedQuestion)) {
                    onChange([...fields, selectedQuestion]);
                }
            }
            setSelectedQuestion('');
            setShowAddForm(false);
        }
    };

    const removeField = (index: number) => {
        const updatedFields = fields.filter((_: any, i: number) => i !== index);
        onChange(updatedFields);
        // Show add form if no fields left
        if (updatedFields.length === 0) {
            setShowAddForm(true);
        }
    };

    const updateRoleMapping = (fieldIndex: number, role: string, values: string[]) => {
        const updatedFields = [...fields];
        updatedFields[fieldIndex].values[role] = values;
        onChange(updatedFields);
    };

    return (
        <Box>
            {/* Current Fields */}
            {fields.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Current {label}:</Typography>
                    {fields.map((field: any, index: number) => (
                        <Box key={index} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                    {(() => {
                                        const questionId = allowValueMapping ? field.questionNum : field;
                                        const questionData = availableQuestions.find(q => q.id === questionId);
                                        return (
                                            <>
                                                <strong>Question {questionId}</strong><br />
                                                {questionData?.title && (
                                                    <span style={{ color: '#666', fontSize: '0.9em' }}>
                                                        {questionData.title}
                                                    </span>
                                                )}
                                            </>
                                        );
                                    })()}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => removeField(index)}
                                    color="error"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>

                            {allowValueMapping && field.values && (
                                <Box sx={{ pl: 2 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Role Mappings (select which answer options map to each role):
                                    </Typography>
                                    {(() => {
                                        // Find the question to get its options
                                        const questionData = availableQuestions.find(q => q.id === field.questionNum);
                                        const hasOptions = questionData?.options && questionData.options.length > 0;

                                        if (hasOptions) {
                                            const roleLabels: { [key: string]: string } = {
                                                'Decide': 'Decision Maker',
                                                'Recommend': 'Recommender/Influencer',
                                                'Evaluate': 'Technical Evaluator',
                                                'Finance': 'Budget Approver',
                                                'Implement': 'Implementation Lead',
                                                'None': 'Not Involved'
                                            };

                                            return Object.entries(field.values).map(([role, values]: [string, any]) => (
                                                <Box key={role} sx={{ mt: 2 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                                                        {roleLabels[role] || role}:
                                                    </Typography>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Select answers for {roleLabels[role] || role}</InputLabel>
                                                        <Select
                                                            multiple
                                                            value={Array.isArray(values) ? values : []}
                                                            onChange={(e) => {
                                                                const selectedValues = typeof e.target.value === 'string'
                                                                    ? e.target.value.split(',')
                                                                    : e.target.value;
                                                                updateRoleMapping(index, role, selectedValues);
                                                            }}
                                                            label={`Select answers for ${roleLabels[role] || role}`}
                                                            renderValue={(selected) => (
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                    {(selected as string[]).map((value) => (
                                                                        <Chip key={value} label={value} size="small" />
                                                                    ))}
                                                                </Box>
                                                            )}
                                                        >
                                                            {questionData!.options!.map((option) => (
                                                                <MenuItem key={option} value={option}>
                                                                    {option}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Box>
                                            ));
                                        } else {
                                            // Fallback to text input for questions without predefined options
                                            return Object.entries(field.values).map(([role, values]: [string, any]) => (
                                                <Box key={role} sx={{ mt: 1 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                                        {role}:
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        placeholder={`Answer values that indicate "${role}" role (comma-separated)`}
                                                        value={Array.isArray(values) ? values.join(', ') : ''}
                                                        onChange={(e) => {
                                                            const newValues = e.target.value.split(',').map(v => v.trim()).filter(v => v);
                                                            updateRoleMapping(index, role, newValues);
                                                        }}
                                                        sx={{ mt: 0.5 }}
                                                    />
                                                </Box>
                                            ));
                                        }
                                    })()}
                                </Box>
                            )}
                        </Box>
                    ))}
                </Box>
            )}

            {/* Add New */}
            {showAddForm ? (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControl sx={{ minWidth: 300 }}>
                        <InputLabel>Add Question</InputLabel>
                        <Select
                            value={selectedQuestion}
                            label="Add Question"
                            onChange={(e) => setSelectedQuestion(e.target.value)}
                        >
                            {availableQuestions.map(question => (
                                <MenuItem key={question.id} value={question.id}>
                                    {question.id}: {question.title}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button variant="outlined" onClick={addField} disabled={!selectedQuestion}>
                        Add
                    </Button>
                    {fields.length > 0 && (
                        <Button
                            variant="text"
                            onClick={() => {
                                setShowAddForm(false);
                                setSelectedQuestion('');
                            }}
                        >
                            Cancel
                        </Button>
                    )}
                </Box>
            ) : (
                <Box sx={{ mt: 1 }}>
                    <Button
                        variant="text"
                        startIcon={<AddIcon />}
                        onClick={() => setShowAddForm(true)}
                        sx={{
                            color: 'primary.main',
                            textTransform: 'none',
                            fontSize: '0.9rem'
                        }}
                    >
                        Add another mapping
                    </Button>
                </Box>
            )}
        </Box>
    );
};

// Question Edit Dialog Component
const QuestionEditDialog = ({ open, onClose, question, onSave }: { open: boolean; onClose: () => void; question: any; onSave: (updates: Record<string, any>) => void }) => {
    const [formData, setFormData] = useState({
        custom_title: '',
        custom_description: '',
        visible: true,
        required: false,
        show_title: true,
        orientation: 'vertical',
        css_class: ''
    });

    useEffect(() => {
        if (question) {
            setFormData({
                custom_title: question.custom_title || '',
                custom_description: question.custom_description || '',
                visible: question.visible !== false,
                required: question.required || false,
                show_title: question.display_options?.show_title !== false,
                orientation: question.display_options?.orientation || 'vertical',
                css_class: question.display_options?.css_class || ''
            });
        }
    }, [question]);

    const handleSave = () => {
        onSave({
            custom_title: formData.custom_title || null,
            custom_description: formData.custom_description || null,
            visible: formData.visible,
            required: formData.required,
            validation_rules: question?.validation_rules || [],
            display_options: {
                show_title: formData.show_title,
                css_class: formData.css_class,
                orientation: formData.orientation
            }
        });
    };

    const renderBasicSettings = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
                fullWidth
                label="Custom Title"
                value={formData.custom_title}
                onChange={(e) => setFormData(prev => ({ ...prev, custom_title: e.target.value }))}
                helperText="Leave empty to use original question title"
            />
            <TextField
                fullWidth
                label="Custom Description"
                value={formData.custom_description}
                onChange={(e) => setFormData(prev => ({ ...prev, custom_description: e.target.value }))}
                multiline
                rows={2}
            />
            <TextField
                fullWidth
                label="CSS Class"
                value={formData.css_class}
                onChange={(e) => setFormData(prev => ({ ...prev, css_class: e.target.value }))}
            />
            <FormControl fullWidth>
                <InputLabel>Orientation</InputLabel>
                <Select
                    value={formData.orientation}
                    label="Orientation"
                    onChange={(e) => setFormData(prev => ({ ...prev, orientation: e.target.value }))}
                >
                    <MenuItem value="vertical">Vertical</MenuItem>
                    <MenuItem value="horizontal">Horizontal</MenuItem>
                </Select>
            </FormControl>
            <FormControlLabel
                control={
                    <Switch
                        checked={formData.visible}
                        onChange={(e) => setFormData(prev => ({ ...prev, visible: e.target.checked }))}
                    />
                }
                label="Visible"
            />
            <FormControlLabel
                control={
                    <Switch
                        checked={formData.required}
                        onChange={(e) => setFormData(prev => ({ ...prev, required: e.target.checked }))}
                    />
                }
                label="Required"
            />
            <FormControlLabel
                control={
                    <Switch
                        checked={formData.show_title}
                        onChange={(e) => setFormData(prev => ({ ...prev, show_title: e.target.checked }))}
                    />
                }
                label="Show Title"
            />
        </Box>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Question</DialogTitle>
            <DialogContent>
                {renderBasicSettings()}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained">Save</Button>
            </DialogActions>
        </Dialog>
    );
};

